import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomEditable, CustomSlate, useRichSlateEditor } from 'src/components/RichSlate';
import { TextAndWordCounter } from 'src/components/TextAndWordCounter';
import { ASSET_TYPE } from 'src/data/asset';
import { createEditorInstanceImperativeHandle } from 'src/lib/slate';

import { EditorSkeleton } from './components/EditorSkeleton';
import { InsightsLeaf } from './components/InsightsLeaf';
import { useContentCounterData } from './useContentCounterData';
import { useDecorate } from './useDecorate';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)).isRequired,
  content: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  markup: PropTypes.arrayOf(PropTypes.shape()),
  onChange: PropTypes.func,
  detectedBrandTerms: PropTypes.arrayOf(PropTypes.shape()),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  setEditor: PropTypes.func,
  onCopy: PropTypes.func,
  onEdit: PropTypes.func,
  editMode: PropTypes.bool,
  isAppModeInfinity: PropTypes.bool,
  loading: PropTypes.bool,
};

const defaultProps = {
  className: null,
  classes: null,
  markup: null,
  onChange: null,
  detectedBrandTerms: [],
  disabled: false,
  readOnly: false,
  setEditor: null,
  onCopy: null,
  onEdit: null,
  editMode: false,
  isAppModeInfinity: false,
  loading: false,
};

export const TextEditorRichSlate = forwardRef(
  (
    {
      className,
      classes,
      assetType,
      content,
      markup,
      onChange,
      detectedBrandTerms,
      disabled,
      readOnly,
      setEditor,
      onCopy,
      onEdit,
      editMode,
      isAppModeInfinity,
      loading,
    },
    ref
  ) => {
    const styles = useStyles({ classes });
    const editorInstance = useRichSlateEditor();
    const [focused, setFocused] = useState(false);

    const counter = useContentCounterData(assetType, content);
    const decorate = useDecorate(editorInstance, { detectedBrandTerms, positionedMarks: markup });

    useEffect(() => {
      if (setEditor) {
        setEditor(editorInstance);
      }
    }, [editorInstance, setEditor]);

    useImperativeHandle(ref, () => createEditorInstanceImperativeHandle(editorInstance), [
      editorInstance,
    ]);

    const handleValueChange = useCallback(
      (value) => {
        if (onChange) {
          onChange(value, editorInstance.operations);
        }
      },
      [onChange, editorInstance]
    );

    const handleFocusChange = useCallback((value) => {
      setFocused(value);
    }, []);

    return (
      <div
        className={cx(styles.root, className, {
          [styles.focused]: focused,
          [styles.empty]: counter.isEmpty,
          [styles.readOnly]: readOnly,
          [styles.extraPaddingBottom]: editMode || isAppModeInfinity,
        })}
        data-testid="TextEditor"
      >
        <div
          className={styles.editorWrapper}
          onClick={onEdit}
        >
          <div className={styles.outline} />

          <CustomSlate
            editor={editorInstance}
            initialValue={content}
            onValueChange={handleValueChange}
          >
            <CustomEditable
              className={cx(styles.editor, { [styles.loadingEditor]: loading })}
              onFocusChange={handleFocusChange}
              decorate={decorate}
              readOnly={disabled || readOnly}
              onCopy={onCopy}
              onCut={onCopy}
              renderLeaf={(props) => <InsightsLeaf {...props} />}
            />
            {loading && <EditorSkeleton className={styles.loader} />}
          </CustomSlate>

          <div className={styles.footer}>
            <TextAndWordCounter
              className={styles.textCounter}
              charsCount={counter.charsCount}
              maxChars={counter.maxChars}
              wordsCount={counter.wordsCount}
              maxWords={counter.maxWords}
              strict={counter.isStrict}
              displayMaxChars={counter.isStrict}
            />
          </div>
        </div>
      </div>
    );
  }
);

TextEditorRichSlate.displayName = 'TextEditorRichSlate';
TextEditorRichSlate.propTypes = propTypes;
TextEditorRichSlate.defaultProps = defaultProps;
